/**
 * `SessionRepository` bằng IndexedDB — Prompt.md §27, §7 (quyết định #15).
 *
 * Mọi thao tác đều **không được throw** ra ngoài: repository này bị gọi từ vòng lặp phiên học,
 * và một lần ghi thất bại vì hết quota không được phép kết thúc phiên học của trẻ. Thất bại được
 * báo qua `onError` để UI hiện cảnh báo một lần.
 */

import {
  InMemorySessionRepository,
  type SessionRepository,
  type SessionSnapshot,
} from "@studystraight/session-core";
import type { StudySession } from "@studystraight/shared-types";

import { PENDING_SNAPSHOT_KEY, openStudyStraightDB, type StudyStraightDatabase } from "./db.js";

export interface StorageStatus {
  /** `false` → UI phải hiện `storageMessages.unavailable`. */
  persistent: boolean;
  reason?: string;
}

export interface IndexedDBRepositoryOptions {
  /** Gọi khi một thao tác thất bại. Dùng để hiện cảnh báo, không dùng để retry. */
  onError?: (operation: string, error: unknown) => void;
}

export class IndexedDBSessionRepository implements SessionRepository {
  private constructor(
    private readonly db: StudyStraightDatabase,
    private readonly options: IndexedDBRepositoryOptions,
  ) {}

  /**
   * Mở repository, tự fallback sang in-memory khi IndexedDB không dùng được.
   *
   * Trả kèm `status` thay vì throw: app vẫn phải chạy được ở Safari private mode (phụ huynh mở
   * link trong tab riêng tư là chuyện thường), chỉ là không lưu lịch sử.
   */
  static async create(
    options: IndexedDBRepositoryOptions = {},
  ): Promise<{ repository: SessionRepository; status: StorageStatus }> {
    try {
      const db = await openStudyStraightDB();
      return {
        repository: new IndexedDBSessionRepository(db, options),
        status: { persistent: true },
      };
    } catch (error) {
      return {
        repository: new InMemorySessionRepository(),
        status: {
          persistent: false,
          reason: error instanceof Error ? error.message : String(error),
        },
      };
    }
  }

  async save(session: StudySession): Promise<void> {
    await this.guard("save", async () => {
      await this.db.put("sessions", session);
      // Phiên đã lưu xong thì snapshot dở dang của nó không còn ý nghĩa; để lại sẽ khiến lần mở
      // app sau hỏi "phiên trước bị ngắt, lưu không?" cho một phiên đã lưu rồi.
      await this.db.delete("snapshots", PENDING_SNAPSHOT_KEY);
    });
  }

  async getById(id: string): Promise<StudySession | null> {
    return (await this.guard("getById", () => this.db.get("sessions", id))) ?? null;
  }

  async getAll(): Promise<readonly StudySession[]> {
    const sessions = await this.guard("getAll", () =>
      this.db.getAllFromIndex("sessions", "by-startTime"),
    );
    // Index cho thứ tự tăng dần; History cần mới nhất trước.
    return sessions === undefined ? [] : sessions.reverse();
  }

  async saveSnapshot(snapshot: SessionSnapshot): Promise<void> {
    await this.guard("saveSnapshot", () =>
      this.db.put("snapshots", snapshot, PENDING_SNAPSHOT_KEY),
    );
  }

  async getPendingSnapshot(): Promise<SessionSnapshot | null> {
    const snapshot = await this.guard("getPendingSnapshot", () =>
      this.db.get("snapshots", PENDING_SNAPSHOT_KEY),
    );
    return snapshot?.finalized === false ? snapshot : null;
  }

  async clearSnapshot(): Promise<void> {
    await this.guard("clearSnapshot", () => this.db.delete("snapshots", PENDING_SNAPSHOT_KEY));
  }

  async deleteAll(): Promise<void> {
    await this.guard("deleteAll", async () => {
      await this.db.clear("sessions");
      await this.db.clear("snapshots");
    });
  }

  /** Một chỗ duy nhất bắt lỗi: quota, private mode, database bị xoá giữa phiên. */
  private async guard<T>(operation: string, run: () => Promise<T>): Promise<T | undefined> {
    try {
      return await run();
    } catch (error) {
      this.options.onError?.(operation, error);
      return undefined;
    }
  }
}
